import React, { createContext, useContext, useState, ReactNode } from "react";

export type Language = "fr" | "en";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children,
}) => {
  const [language, setLanguage] = useState<Language>("fr");

  const t = (key: string): string => {
    const keys = key.split(".");
    let value: any = translations[language];

    for (const k of keys) {
      value = value?.[k];
    }

    return value || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

// Translations object - Optimized with only used keys
const translations = {
  fr: {
    nav: {
      home: "Accueil",
      about: "À propos",
      projects: "Projets",
      experience: "Expérience",
      contact: "Contact",
    },
    hero: {
      title: "Ingénieur logiciel, Développeur Fullstack",
      bio: "Ingénieur passionné par la création d'applications web modernes et intuitives. J'aime relever de nouveaux défis techniques et apprendre les dernières technologies du web.",
      downloadCv: "Télécharger CV",
      viewProjects: "Voir mes projets",
      contact: "Me contacter",
    },
    about: {
      title: "À propos de moi",
      subtitle: "Qui suis-je ?",
      description: [
        "Développeur web passionné, je me suis découvert une véritable fascination pour la création d'applications et de sites web modernes.",
        "Après avoir obtenu mon diplôme d'ingénieur informatique, je continue d'apprendre et de me perfectionner dans les technologies front-end et back-end.",
        "Ma curiosité naturelle et ma rigueur me poussent à toujours chercher les meilleures solutions techniques tout en gardant l'expérience utilisateur au cœur de mes préoccupations.",
        "Je suis à la recherche d'opportunités pour mettre en pratique mes compétences et contribuer à des projets stimulants au sein d'une équipe dynamique.",
      ],
      skills: "Compétences",
      stats: {
        experience: "Années d'expérience",
        projects: "Projets réalisés",
        technologies: "Technologies",
      },
    },
    projects: {
      title: "Mes Projets",
      viewCode: "Voir code",
      viewDemo: "Voir démo",
    },
    experience: {
      title: "Mon Parcours",
      present: "Présent",
      education: "Formation",
      work: "Expérience Professionnelle",
      workDescription:
        "Mon parcours professionnel et les projets sur lesquels j'ai travaillé",
      educationDescription: "Mon parcours académique et mes certifications",
    },
    contact: {
      title: "Me Contacter",
      subtitle: "Discutons de votre projet",
      description:
        "Je suis toujours intéressé par de nouveaux défis et opportunités. N'hésitez pas à me contacter pour discuter de vos projets ou simplement pour échanger sur les technologies qui nous passionnent.",
      form: {
        name: "Nom",
        email: "Email",
        subject: "Sujet",
        message: "Message",
        send: "Envoyer le message",
        sending: "Envoi en cours...",
      },
      info: {
        email: "Email",
        phone: "Téléphone",
      },
      documents: "Documents",
      downloadCv: "Télécharger CV",
      success:
        "Votre message a été envoyé avec succès ! Je vous répondrai dans les plus brefs délais.",
      error:
        "Une erreur est survenue lors de l'envoi. Veuillez réessayer ou me contacter directement.",
      sendMessage: "Envoyez-moi un message",
    },
    footer: {
      rights: "Tous droits réservés.",
      description:
        "Développeur passionné, toujours à la recherche de nouveaux défis et d'opportunités pour créer des solutions innovantes.",
      navigation: "Navigation",
    },
    data: {
      personalInfo: {
        title: "Ingénieur logiciel, Développeur Fullstack",
        bio: "Ingénieur passionné par la création d'applications web modernes et intuitives. J'aime relever de nouveaux défis techniques et apprendre les dernières technologies du web. Mon objectif est de contribuer à des projets innovants tout en développant mes compétences.",
      },
      skills: {
        categories: {
          frontend: "Frontend",
          backend: "Backend & Base de données",
          tools: "Outils de développement",
          soft: "Compétences transversales",
        },
        softSkills: {
          learning: "Apprentissage rapide",
          curiosity: "Curiosité technique",
          teamwork: "Travail en équipe",
          rigor: "Rigueur",
          adaptability: "Adaptabilité",
        },
      },
      positions: {
        apprenticeEngineer: "Ingénieur en alternance",
        internDeveloper: "Stagiaire Développeur",
      },
    },
  },
  en: {
    nav: {
      home: "Home",
      about: "About",
      projects: "Projects",
      experience: "Experience",
      contact: "Contact",
    },
    hero: {
      title: "Software Engineer, Full Stack Developer",
      bio: "Passionate engineer focused on creating modern and intuitive web applications. I enjoy tackling new technical challenges and learning the latest web technologies.",
      downloadCv: "Download Resume",
      viewProjects: "View my projects",
      contact: "Contact me",
    },
    about: {
      title: "About Me",
      subtitle: "Who am I?",
      description: [
        "Passionate web developer, I discovered a true fascination for creating modern applications and websites.",
        "After obtaining my computer engineering degree, I continue to learn and improve my skills in front-end and back-end technologies.",
        "My natural curiosity and rigor drive me to always seek the best technical solutions while keeping user experience at the heart of my concerns.",
        "I am looking for opportunities to put my skills into practice and contribute to stimulating projects within a dynamic team.",
      ],
      skills: "Skills",
      stats: {
        experience: "Years of experience",
        projects: "Projects completed",
        technologies: "Technologies",
      },
    },
    projects: {
      title: "My Projects",
      viewCode: "View code",
      viewDemo: "View demo",
    },
    experience: {
      title: "My Journey",
      present: "Present",
      education: "Education",
      work: "Professional Experience",
      workDescription:
        "My professional journey and the projects I have worked on",
      educationDescription: "My academic background and certifications",
    },
    contact: {
      title: "Contact Me",
      subtitle: "Let's discuss your project",
      description:
        "I'm always interested in new challenges and opportunities. Don't hesitate to contact me to discuss your projects or simply to exchange ideas about the technologies that fascinate us.",
      form: {
        name: "Name",
        email: "Email",
        subject: "Subject",
        message: "Message",
        send: "Send message",
        sending: "Sending...",
      },
      info: {
        email: "Email",
        phone: "Phone",
      },
      documents: "Documents",
      downloadCv: "Download Resume",
      success:
        "Your message has been sent successfully! I will reply to you as soon as possible.",
      error:
        "An error occurred while sending. Please try again or contact me directly.",
      sendMessage: "Send me a message",
    },
    footer: {
      rights: "All rights reserved.",
      description:
        "Passionate developer, always looking for new challenges and opportunities to create innovative solutions.",
      navigation: "Navigation",
    },
    data: {
      personalInfo: {
        title: "Software Engineer, Full Stack Developer",
        bio: "Passionate engineer focused on creating modern and intuitive web applications. I enjoy tackling new technical challenges and learning the latest web technologies. My goal is to contribute to innovative projects while developing my skills.",
      },
      skills: {
        categories: {
          frontend: "Frontend",
          backend: "Backend & Database",
          tools: "Development Tools",
          soft: "Soft Skills",
        },
        softSkills: {
          learning: "Quick Learning",
          curiosity: "Technical Curiosity",
          teamwork: "Teamwork",
          rigor: "Rigor",
          adaptability: "Adaptability",
        },
      },
      positions: {
        apprenticeEngineer: "Apprentice Engineer",
        internDeveloper: "Developer Intern",
      },
    },
  },
};
